import { usageTypeLabel } from 'admin/constants/usageType';
import React from 'react';
import ReactModal from 'react-modal';
import styled from 'styled-components';
import { Button } from '../../atoms/Buttons';
import FA from '../../atoms/FontAwesome';
import Headings from '../../atoms/Headings';

export type ConfirmAction = 'approve' | 'reject' | 'review' | 'hold' | 'unhold' | 'save';

type Props = {
  isOpen: boolean;
  action: ConfirmAction;
  organizationCode: string | null;
  privateMoneyNames: string[];
  shopName: string;
  shopNameFurigana: string;
  postalCode: string;
  address: string;
  tel: string;
  email: string;
  contactPersonName: string;
  contactPersonNameFurigana: string;
  usageType: string;
  tagSummary: {
    groupId: string;
    groupName: string;
    itemNames: string[];
    changed: boolean;
    revised: boolean;
  }[];
  changedFields?: Record<string, boolean>;
  revisedFields?: Record<string, boolean>;
  onClose: () => void;
  onConfirm: () => void;
  isSubmitting: boolean;
};

const modalStyles = {
  overlay: {
    zIndex: 9999,
    backgroundColor: 'rgba(0, 0, 0, 0.5)',
  },
  content: {
    padding: 40,
    top: '50%',
    left: '50%',
    right: 'auto',
    bottom: 'auto',
    marginRight: '-30%',
    transform: 'translate(-50%, -50%)',
    maxWidth: 600,
    maxHeight: '95vh',
    overflow: 'auto',
  },
};

const ModalHeader = styled(Headings.h4)`
  font-size: 20px;
  text-align: center;
  margin: 0;
  width: 100%;
  font-weight: 700;
`;

const WarningMessage = styled.div`
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 8px;
  color: ${({ theme }) => theme.semanticColors.error};
  margin-top: 16px;
  font-size: 14px;
  font-weight: normal;
`;

const ModalContent = styled.div`
  margin: 40px 0;
`;

const ModalFooter = styled.div`
  display: flex;
  align-items: center;
  justify-content: center;
  width: 100%;
  gap: 24px;
`;

const Section = styled.section`
  display: flex;
  flex-direction: column;
  gap: 16px;
`;

const SubSection = styled.div`
  display: flex;
  gap: 24px;
`;

const SectionLabel = styled.label`
  width: 180px;
  color: #9f9f9f;
  font-size: 16px;
  font-style: normal;
  font-weight: 700;
  line-height: 24px;
  margin: 0;
  flex-shrink: 0;
  display: flex;
  align-items: center;
  gap: 8px;
  flex-wrap: wrap;
`;

const EditedBadgeContainer = styled.span`
  border-radius: 4px;
  background: #21bf25;
  width: 64px;
  color: #fff;
  font-size: 12px;
  font-style: normal;
  font-weight: 700;
  line-height: 20px;
  text-align: center;
  display: inline-block;
`;

const EditedBadge = () => <EditedBadgeContainer>変更あり</EditedBadgeContainer>;

const RevisedBadgeContainer = styled.span`
  border-radius: 4px;
  background: #f38d22;
  min-width: 64px;
  color: #fff;
  font-size: 12px;
  font-style: normal;
  font-weight: 700;
  line-height: 20px;
  text-align: center;
  display: inline-block;
  margin-left: 8px;
`;

const RevisedBadge = () => <RevisedBadgeContainer>修正あり</RevisedBadgeContainer>;

const InfoText = styled.p`
  margin: 0;
  color: #222;
  font-size: 16px;
  font-style: normal;
  font-weight: 400;
  line-height: 24px;
  flex: 1;
  word-break: break-word;
`;

const ConfirmModal = ({
  isOpen,
  action,
  organizationCode,
  privateMoneyNames,
  shopName,
  shopNameFurigana,
  postalCode,
  address,
  tel,
  email,
  contactPersonName,
  contactPersonNameFurigana,
  usageType,
  tagSummary,
  changedFields = {},
  revisedFields = {},
  onClose,
  onConfirm,
  isSubmitting,
}: Props) => {
  const headerText = {
    approve: 'この申請を承認します。よろしいですか？',
    reject: 'この申請を却下します。よろしいですか？',
    review: 'この申請を審査中にします。よろしいですか？',
    hold: 'この申請を保留にします。よろしいですか？',
    unhold: 'この申請の保留を解除します。よろしいですか？',
    save: 'この申請内容を保存します。よろしいですか？',
  }[action];
  const confirmButtonText = {
    approve: '承認',
    reject: '却下',
    review: '審査中にする',
    hold: '保留にする',
    unhold: '保留解除',
    save: '保存',
  }[action];
  const resultStatusText = {
    approve: '申請承認済',
    reject: '却下',
    review: '審査中',
    hold: '保留中',
    unhold: '申請受付済',
    save: '審査中',
  }[action];
  // approve / reject は確定操作で元に戻せない。review / hold / unhold は復帰可能。
  const isIrreversible = action === 'approve' || action === 'reject';

  return (
    <ReactModal
      isOpen={isOpen}
      onRequestClose={onClose}
      shouldCloseOnOverlayClick={!isSubmitting}
      closeTimeoutMS={200}
      ariaHideApp={false}
      style={modalStyles}
    >
      <ModalHeader>
        {headerText}
        {isIrreversible && (
          <WarningMessage>
            <FA icon="exclamation-triangle" />
            <span>この操作は元に戻せません</span>
          </WarningMessage>
        )}
      </ModalHeader>

      <ModalContent>
        <Section>
          <SubSection>
            <SectionLabel>進捗状態</SectionLabel>
            <InfoText>{resultStatusText}</InfoText>
          </SubSection>

          <SubSection>
            <SectionLabel>組織名</SectionLabel>
            <InfoText>{organizationCode || '-'}</InfoText>
          </SubSection>

          <SubSection>
            <SectionLabel>
              加盟するマネー {changedFields['private_money_ids'] && <EditedBadge />}
              {revisedFields['private_money_ids'] && <RevisedBadge />}
            </SectionLabel>
            <InfoText>{privateMoneyNames.join(', ') || '-'}</InfoText>
          </SubSection>

          <SubSection>
            <SectionLabel>
              店舗名 {changedFields['shop_name'] && <EditedBadge />}
              {revisedFields['shop_name'] && <RevisedBadge />}
            </SectionLabel>
            <InfoText>{shopName || '-'}</InfoText>
          </SubSection>

          <SubSection>
            <SectionLabel>
              店舗名（フリガナ） {changedFields['shop_name_furigana'] && <EditedBadge />}
              {revisedFields['shop_name_furigana'] && <RevisedBadge />}
            </SectionLabel>
            <InfoText>{shopNameFurigana || '-'}</InfoText>
          </SubSection>

          <SubSection>
            <SectionLabel>
              郵便番号 {changedFields['postal_code'] && <EditedBadge />}
              {revisedFields['postal_code'] && <RevisedBadge />}
            </SectionLabel>
            <InfoText>{postalCode || '-'}</InfoText>
          </SubSection>

          <SubSection>
            <SectionLabel>
              住所 {changedFields['address'] && <EditedBadge />}
              {revisedFields['address'] && <RevisedBadge />}
            </SectionLabel>
            <InfoText>{address || '-'}</InfoText>
          </SubSection>

          <SubSection>
            <SectionLabel>
              電話番号 {changedFields['tel'] && <EditedBadge />}
              {revisedFields['tel'] && <RevisedBadge />}
            </SectionLabel>
            <InfoText>{tel || '-'}</InfoText>
          </SubSection>

          <SubSection>
            <SectionLabel>
              メールアドレス {changedFields['email'] && <EditedBadge />}
              {revisedFields['email'] && <RevisedBadge />}
            </SectionLabel>
            <InfoText>{email || '-'}</InfoText>
          </SubSection>

          <SubSection>
            <SectionLabel>
              担当者名 {changedFields['contact_person_name'] && <EditedBadge />}
              {revisedFields['contact_person_name'] && <RevisedBadge />}
            </SectionLabel>
            <InfoText>{contactPersonName || '-'}</InfoText>
          </SubSection>

          <SubSection>
            <SectionLabel>
              担当者名（フリガナ）{' '}
              {changedFields['contact_person_name_furigana'] && <EditedBadge />}
              {revisedFields['contact_person_name_furigana'] && <RevisedBadge />}
            </SectionLabel>
            <InfoText>{contactPersonNameFurigana || '-'}</InfoText>
          </SubSection>

          <SubSection>
            <SectionLabel>
              店舗形態 {changedFields['usage_type'] && <EditedBadge />}
              {revisedFields['usage_type'] && <RevisedBadge />}
            </SectionLabel>
            <InfoText>{usageTypeLabel(usageType) || '-'}</InfoText>
          </SubSection>

          {tagSummary.map((entry) => (
            <SubSection key={entry.groupId}>
              <SectionLabel>
                {entry.groupName} {entry.changed && <EditedBadge />}
                {entry.revised && <RevisedBadge />}
              </SectionLabel>
              <InfoText>{entry.itemNames.join('、') || '-'}</InfoText>
            </SubSection>
          ))}
        </Section>
      </ModalContent>

      <ModalFooter>
        <Button variant="neutral" onClick={onClose} disabled={isSubmitting}>
          キャンセル
        </Button>
        <Button variant="contained" onClick={onConfirm} disabled={isSubmitting}>
          {isSubmitting ? '処理中...' : confirmButtonText}
        </Button>
      </ModalFooter>
    </ReactModal>
  );
};

export default ConfirmModal;
